import React, { useState } from 'react';
import genStyles from '../generale.module.css';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    apiCollectionState,
    collectionsShareState,
    collectionsState,
    reloadCollectionState,
    reloadEntiteState,
    userDataState
} from '../../atoms/CollectionState';
import CollectionService from '../../service/CollectionService';

// Au-delà, l'affichage sur la carte est lent : un avertissement accompagne la case.
const SHOW_ON_MAP_LIMIT = 5000;

// Droit « ajout » d'un partage (booléen PostgreSQL, selon le pilote : true ou 't').
const canAddToShared = (share) => share?.add === true || share?.add === 't' || share?.add === 1;

/**
 * Fenêtre d'import des parcelles de l'assistant foncier : choix ou création de la collection.
 * Les géométries sont récupérées et enregistrées par le serveur (route import-parcelles).
 */
export default function ImportParcellesForm({ request, onClose, onImported }) {
    const api_collection = useRecoilValue(apiCollectionState);
    const user_data = useRecoilValue(userDataState);
    const collections = useRecoilValue(collectionsState);
    const sharedCollections = useRecoilValue(collectionsShareState).filter(canAddToShared);
    const setReloadCollection = useSetRecoilState(reloadCollectionState);
    const setReloadEntite = useSetRecoilState(reloadEntiteState);
    const [collectionChoice, setCollectionChoice] = useState("new");
    const [collectionName, setCollectionName] = useState((request.label || '').slice(0, 100));
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);

    const count = Number.isFinite(request.count) ? request.count : null;
    const [showOnMap, setShowOnMap] = useState(false);

    const onCancel = () => {
        if (running) return;
        request.resolve({ cancelled: true });
        onClose();
    };

    const onValidate = async () => {
        // Valeur du choix : "new", "own:{id}" ou "shared:{id}".
        const isNew = collectionChoice === "new";
        const [kind, selectedId] = isNew ? ['new', null] : collectionChoice.split(':');
        const shared = kind === 'shared';
        if (isNew && collectionName.trim() === '') {
            setError("Donnez un nom à la nouvelle collection.");
            return;
        }
        setError(null);
        setRunning(true);

        const urlApi = api_collection + user_data.account.port + '/';
        const userId = user_data.user.id;
        let collectionId = selectedId;
        let name = (shared ? sharedCollections : collections).find(c => String(c.id) === String(selectedId))?.nom_collection ?? '';

        if (isNew) {
            const created = await CollectionService.addCollection(urlApi, userId, collectionName.trim());
            if (!created?.id) {
                setRunning(false);
                setError(created?.message || "La collection n'a pas pu être créée.");
                return;
            }
            collectionId = created.id;
            name = collectionName.trim();
        }

        const body = request.filter ? { filter: request.filter } : { idus: request.idus };
        const result = await CollectionService.importParcelles(urlApi, userId, collectionId, body);

        if (result?.error) {
            // Une collection créée pour l'import et restée vide est supprimée.
            if (isNew) await CollectionService.deleteCollection(urlApi, userId, collectionId);
            setReloadCollection(true);
            request.reject(new Error(result.message));
            onClose();
            return;
        }

        onImported?.({ collectionId, shared, show: showOnMap });
        setReloadEntite(true);
        request.resolve({
            imported: result.imported,
            duplicates: result.duplicates,
            collectionId,
            collectionName: name,
            shown: showOnMap
        });
        onClose();
    };

    return (
        <div className={`${genStyles.importParcFrm} row mb0`}>
            <div className="col s12">
                <p className={`${genStyles.fontSize22} ${genStyles.uppercase} ${genStyles.bold} center-align bgcolor_first-text`}>
                    Ajouter à une collection
                </p>
                <p className={`${genStyles.fontSize15} black-text`}>
                    {count != null
                        ? `${count.toLocaleString('fr-FR')} parcelle${count > 1 ? 's' : ''} trouvée${count > 1 ? 's' : ''} par l'assistant foncier.`
                        : `Parcelles trouvées par l'assistant foncier.`}
                </p>
            </div>
            <div className="col s12 mb10">
                <span className={`${genStyles.fontSize15} bgcolor_first-text`}>Collection</span>
                <select
                    className={genStyles.selectCol}
                    value={collectionChoice}
                    disabled={running}
                    onChange={(e) => setCollectionChoice(e.target.value)}
                >
                    <option value="new">Nouvelle collection</option>
                    {collections.length > 0 && (
                        <optgroup label="Mes collections">
                            {collections.map((element) => (
                                <option key={`own-${element?.id}`} value={`own:${element?.id}`}>
                                    {element?.nom_affiche || element?.nom_collection}
                                </option>
                            ))}
                        </optgroup>
                    )}
                    {sharedCollections.length > 0 && (
                        <optgroup label="Partagées avec moi">
                            {sharedCollections.map((element) => (
                                <option key={`shared-${element?.id}`} value={`shared:${element?.id}`}>
                                    {`${element?.nom_affiche || element?.nom_collection} (partagée)`}
                                </option>
                            ))}
                        </optgroup>
                    )}
                </select>
            </div>
            {collectionChoice === "new" && (
                <div className="col s12 mb10">
                    <span className={`${genStyles.fontSize15} bgcolor_first-text`}>Nom de la collection</span>
                    <input
                        className={genStyles.inputtCol}
                        type="text"
                        placeholder="Nom de la collection"
                        value={collectionName}
                        disabled={running}
                        onChange={(e) => setCollectionName(e.target.value)}
                    />
                </div>
            )}
            <div className="col s12 mb10">
                <label>
                    <input
                        type="checkbox"
                        className="filled-in"
                        checked={showOnMap}
                        disabled={running}
                        onChange={(e) => setShowOnMap(e.target.checked)}
                    />
                    <span className={`${genStyles.fontSize15} black-text`}>Afficher la collection sur la carte après l'import</span>
                </label>
                {count != null && count > SHOW_ON_MAP_LIMIT && (
                    <p className={`${genStyles.fontSize15} grey-text`}>
                        Affichage lent au-delà de {SHOW_ON_MAP_LIMIT.toLocaleString('fr-FR')} parcelles : vous pourrez l'afficher plus tard depuis la liste des collections.
                    </p>
                )}
            </div>
            {error && (
                <div className="col s12 mb10">
                    <span className={`${genStyles.fontSize15} red-text`}>{error}</span>
                </div>
            )}
            {running && (
                <div className="col s12 mb10">
                    <span className={`${genStyles.fontSize15} black-text`}>
                        <i className="fa-solid fa-spinner fa-spin"></i> Import en cours, cela peut prendre jusqu'à une minute…
                    </span>
                </div>
            )}
            <div className="col s12">
                <div className={genStyles.modalActions}>
                    <button
                        className={`${genStyles.modalBtn} ${genStyles.modalBtnSecondary}`}
                        disabled={running}
                        onClick={onCancel}
                    >
                        Annuler
                    </button>
                    <button
                        className={`${genStyles.modalBtn} ${genStyles.modalBtnPrimary}`}
                        disabled={running}
                        onClick={onValidate}
                    >
                        Importer
                    </button>
                </div>
            </div>
        </div>
    );
}
